import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import "./editorialNavigationTabs.css";

/**
 * ============================================
 * EDITORIAL NAVIGATION TABS COMPONENT (HOME)
 * ============================================
 * 
 * Renderiza abas de navegação para todos os feeds editoriais
 * Permite ao usuário explorar facilmente todos os feeds do SOCIALIZA
 */

// Mapa de rotas para cada tipo editorial
const EDITORIAL_FEED_ROUTES = {
  tudo: "/",
  post_feed: "/",
  short_video: "/short-video",
  long_video: "/long-video",
  vertical_clip: "/vertical-clip",
  quick_video: "/quick-video",
  streaming_media: "/streaming",
  gaming: "/gaming",
  apkstore: "/apk-store",
  audio: "/audio",
  music_player: "/music",
  live_audio: "/live-audio",
  live_streaming: "/live-stream",
  movie_portal: "/movie-portal",
  video_bank: "/video-bank",
  threads: "/threads",
  gif: "/gif",
  sticker: "/sticker",
  news_article: "/news",
  blog: "/blog",
  podcast: "/podcast",
  portfolio: "/portfolio",
  library: "/library",
  ebook: "/ebook",
  pdf: "/pdf",
  course: "/course",
  quiz: "/quiz",
  tutorial: "/tutorial",
  estudent: "/estudent",
  school: "/school",
  voting: "/voting",
  esports: "/esports",
  game_review: "/game-review",
  eshop: "/eshop",
  marketplace: "/marketplace",
  restaurant: "/restaurant",
  food_delivery: "/food-delivery",
  booking: "/booking",
  lodging: "/lodging",
  rental: "/rental",
  mobility: "/mobility",
  job_service: "/job-service",
  event: "/event",
  classified: "/classified",
  medical: "/medical",
  ong: "/ong",
  crowdfunding: "/crowdfunding",
  community: "/community",
  petition: "/petition",
  travel: "/travel",
  car: "/car",
  yoga: "/yoga",
  exercise: "/exercise",
  recipe: "/recipe",
  fashion: "/fashion",
  beauty: "/beauty",
};

export default function EditorialNavigationTabs() {
  const { user } = useSelector((state) => ({ ...state }));
  const navigate = useNavigate();
  const [allTypes, setAllTypes] = useState([]);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [visibleTabsCount, setVisibleTabsCount] = useState(8);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const tabsContainerRef = useRef(null);
  const moreButtonRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Carregar tipos editoriais disponíveis
  useEffect(() => {
    fetchEditorialTypes();
  }, []);

  // Responder a mudanças de tamanho para ajustar visibilidade das abas
  useEffect(() => {
    const handleResize = () => {
      adjustVisibleTabsCount();
      checkScrollButtons();
    };

    window.addEventListener("resize", handleResize);
    adjustVisibleTabsCount();
    checkScrollButtons();

    return () => window.removeEventListener("resize", handleResize);
  }, [allTypes]);

  /**
   * Verificar se deve mostrar botões de navegação
   */
  const checkScrollButtons = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
  };

  /**
   * Scroll para esquerda
   */
  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: -200,
      behavior: "smooth",
    });
    setTimeout(checkScrollButtons, 300);
  };

  /**
   * Scroll para direita
   */
  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: 200,
      behavior: "smooth",
    });
    setTimeout(checkScrollButtons, 300);
  };

  /**
   * Ajustar quantas abas mostrar baseado no tamanho da tela
   */
  const adjustVisibleTabsCount = () => {
    if (window.innerWidth < 640) {
      setVisibleTabsCount(4);
    } else if (window.innerWidth < 1024) {
      setVisibleTabsCount(6);
    } else {
      setVisibleTabsCount(8);
    }
  };

  /**
   * Buscar todos os tipos editoriais
   */
  const fetchEditorialTypes = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/editorial/types`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (data.status === "success" && data.data) {
        // Adicionar "Tudo" no início
        const allTypesWithHome = [
          {
            id: "tudo",
            name: "Tudo",
            description: "Ver todos os posts",
            icon: "home",
            color: "#1877f2",
            order: 0,
          },
          ...data.data,
        ];
        setAllTypes(allTypesWithHome);
      } else if (Array.isArray(data)) {
        const allTypesWithHome = [
          {
            id: "tudo",
            name: "Tudo",
            description: "Ver todos os posts",
            icon: "home",
            color: "#1877f2",
            order: 0,
          },
          ...data,
        ];
        setAllTypes(allTypesWithHome);
      }
    } catch (error) {
      console.error("Erro ao buscar tipos editoriais:", error);
    }
  };

  /**
   * Navegar para feed editorial
   */
  const handleNavigate = (typeId) => {
    const route = EDITORIAL_FEED_ROUTES[typeId] || "/feeds";
    navigate(route);
    setShowMoreMenu(false);
  };

  /**
   * Renderizar ícone baseado no tipo
   */
  const getTabIcon = (type) => {
    if (typeof type.icon === "string") {
      const iconMap = {
        home: "🏠",
        post_feed: "📝",
        short_video: "📱",
        long_video: "📹",
        vertical_clip: "🎞️",
        quick_video: "⚡",
        streaming_media: "🎭",
        gaming: "🎮",
        audio: "🎧",
        music_player: "🎶",
        live_audio: "🎙️",
        live_streaming: "📡",
        movie_portal: "🎥",
        video_bank: "📼",
        threads: "💬",
        gif: "🖼️",
        sticker: "😀",
        news_article: "📰",
        blog: "📝",
        podcast: "🎙️",
        portfolio: "💼",
        library: "📚",
        ebook: "📖",
        pdf: "📄",
        course: "🎓",
        quiz: "❓",
        tutorial: "📺",
        estudent: "👨‍🎓",
        school: "🏫",
        voting: "🗳️",
        esports: "🏆",
        game_review: "🎯",
        eshop: "🛍️",
        marketplace: "🛒",
        restaurant: "🍽️",
        food_delivery: "🍕",
        booking: "🏨",
        lodging: "🏡",
        rental: "🚗",
        mobility: "🚕",
        job_service: "💼",
        event: "🎉",
        classified: "📋",
        medical: "⚕️",
        ong: "❤️",
        crowdfunding: "💰",
        community: "👥",
        petition: "✍️",
        travel: "✈️",
        car: "🚗",
        yoga: "🧘",
        exercise: "💪",
        recipe: "🍳",
        fashion: "👗",
        beauty: "💄",
      };
      return iconMap[type.icon] || "📌";
    }

    return type.icon;
  };

  // Abas visíveis no scroll normal
  const visibleTabs = allTypes.slice(0, visibleTabsCount);

  // Abas no menu "Mais"
  const hiddenTabs = allTypes.slice(visibleTabsCount);

  return (
    <div className="editorial_navigation_tabs_container">
      {/* Botão de navegação esquerda */}
      {showLeftArrow && (
        <button
          className="editorial_tabs_nav_btn editorial_tabs_nav_left"
          onClick={scrollLeft}
          title="Navegar para esquerda"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M12.5 15l-5-5 5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Botão de navegação direita */}
      {showRightArrow && (
        <button
          className="editorial_tabs_nav_btn editorial_tabs_nav_right"
          onClick={scrollRight}
          title="Navegar para direita"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M7.5 15l5-5-5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <div className="editorial_navigation_tabs" ref={tabsContainerRef}>
        <div 
          className="editorial_tabs_scroll"
          ref={scrollContainerRef}
          onScroll={checkScrollButtons}
        >
          {/* Abas visíveis */}
          {visibleTabs.map((type) => (
            <button
              key={type.id}
              className="editorial_nav_tab hover1"
              onClick={() => handleNavigate(type.id)}
              style={{
                "--tab-color": type.color,
              }}
              title={type.description}
            >
              <span className="editorial_nav_tab_icon">
                {getTabIcon(type)}
              </span>
              <span className="editorial_nav_tab_name">{type.name}</span>
            </button>
          ))}

          {/* Botão "Mais" se houver abas escondidas */}
          {hiddenTabs.length > 0 && (
            <div className="editorial_nav_tab_more_wrapper">
              <button
                ref={moreButtonRef}
                className={`editorial_nav_tab editorial_nav_tab_more hover1 ${
                  showMoreMenu ? "active" : ""
                }`}
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                title="Mostrar mais feeds"
              >
                <span className="editorial_nav_tab_icon">⋯</span>
                <span className="editorial_nav_tab_name">Mais</span>
              </button>

              {/* Menu dropdown de abas escondidas */}
              {showMoreMenu && (
                <div className="editorial_nav_tabs_more_menu">
                  {hiddenTabs.map((type) => (
                    <button
                      key={type.id}
                      className="editorial_nav_tab_more_item hover1"
                      onClick={() => handleNavigate(type.id)}
                      style={{
                        "--tab-color": type.color,
                      }}
                    >
                      <span className="editorial_nav_tab_icon">
                        {getTabIcon(type)}
                      </span>
                      <span className="editorial_nav_tab_name">{type.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fechar menu ao clicar fora */}
      {showMoreMenu && (
        <div
          className="editorial_nav_tabs_backdrop"
          onClick={() => setShowMoreMenu(false)}
        />
      )}
    </div>
  );
}
